import { Alert, Box, CircularProgress } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Section from '../../components/Main/Section/Section';
import SurveyService from '../../services/SurveyService';
import { ISurvey } from '../../shared/interfaces/ISurvey';
import { indexedDbDraftStorage, ISurveyPreviewDraft } from '../../shared/SurveyPreviewDraft';
import NetPromoterScorePage from '../NetPromoterScore/NetPromoterScorePage';
import MoodBarometerPage from '../MoodBarometer/MoodBarometerPage';
import StandardSurveyPage from '../StandardSurvey/StandardSurveyPage';
import CustomSurveyPage from '../CustomSurvey/CustomSurveyPage';

const SurveyPreviewPage = () => {
  const { draftKey } = useParams();
  const { t } = useTranslation();
  const [draft, setDraft] = useState<ISurveyPreviewDraft | null | undefined>(undefined);
  const previewType = draft?.type;
  const previewSavedSurveyId = draft?.savedSurveyId;

  useEffect(() => {
    if (!draftKey) return setDraft(null);
    indexedDbDraftStorage
      .get(draftKey)
      .then(setDraft)
      .catch(() => setDraft(null));
  }, [draftKey]);

  useEffect(() => {
    if (previewType !== 'CUSTOMSURVEY' || !previewSavedSurveyId) return;
    SurveyService.getSurveyPublic(previewSavedSurveyId, 'customsurvey').then((saved) => {
      if (saved?.questions)
        setDraft(
          (current) =>
            current && { ...current, survey: { ...current.survey, questions: saved.questions } }
        );
    });
  }, [previewType, previewSavedSurveyId]);

  if (draft === undefined)
    return (
      <Section>
        <Box sx={{ display: 'flex', flex: 1, alignItems: 'center' }}>
          <CircularProgress />
        </Box>
      </Section>
    );
  if (!draft) return <Section>{t('survey_preview_not_found')}</Section>;

  const survey = draft.survey as ISurvey & {
    shouldShowPrivacyNotice: boolean;
    privacyNoticeText: string;
    privacyNoticeLink: string;
  };
  const page = {
    NPS: <NetPromoterScorePage previewSurvey={survey} />,
    BAROMETER: <MoodBarometerPage previewSurvey={survey} />,
    STANDARD: <StandardSurveyPage previewSurvey={survey} />,
    CUSTOMSURVEY: <CustomSurveyPage previewSurvey={survey} />,
  }[draft.type];
  if (!page) return <Section>{t('survey_preview_not_found')}</Section>;

  return (
    <>
      {draft.fontDropped && <Alert severity="warning">{t('survey_preview_font_omitted')}</Alert>}
      {draft.type === 'CUSTOMSURVEY' && !draft.savedSurveyId && (
        <Alert severity="info">{t('survey_preview_questions_after_save')}</Alert>
      )}
      {page}
    </>
  );
};

export default SurveyPreviewPage;
